'use client';

import React, { useState, useRef, useEffect } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';
import ScheduleModal from '@/components/ScheduleModal';

// --- Data (Adapted from WhyChooseUs.tsx) ---
const anomalies = [
    {
        id: 'speed',
        icon: 'RocketLaunchIcon',
        title: 'Speed & Performance First',
        desc: 'We obsess over milliseconds. Our architectures are optimized for perfect Core Web Vitals, ensuring your site ranks higher and converts better. No bloat, just raw speed.',
        color: 'indigo'
    },
    {
        id: 'ownership',
        icon: 'LockOpenIcon', // Using appropriate icon for ownership
        title: '100% Ownership',
        desc: 'No vendor lock-in. You own everything we build. We believe in freedom, not holding your code hostage.',
        color: 'fuchsia'
    },
    {
        id: 'seo',
        icon: 'ChartBarIcon',
        title: 'SEO Dominance',
        desc: 'Technical SEO built-in. We structure your site to be loved by search engines from day one.',
        color: 'violet'
    },
    {
        id: 'code',
        icon: 'CodeBracketSquareIcon',
        title: 'Clean, Modern Code',
        desc: 'We write maintainable, type-safe code using the latest standards. React, Next.js, and TypeScript are our native tongue.',
        color: 'cyan'
    },
    {
        id: 'gmb',
        icon: 'MapIcon',
        title: 'Google Profile Mastery',
        desc: 'We optimize your GMB profile to dominate local search results and drive foot traffic.',
        color: 'emerald'
    },
    {
        id: 'cta',
        icon: 'SparklesIcon',
        title: 'Ready to Scale?',
        desc: "Let's build something extraordinary together.",
        color: 'indigo',
        isCta: true
    }
];

const MobileWhyChooseUs = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Track scroll to update active index
    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const scrollLeft = scrollContainerRef.current.scrollLeft;
            const width = scrollContainerRef.current.offsetWidth;
            const index = Math.round(scrollLeft / width);
            setActiveIndex(index);
        }
    };

    return (
        <section className="py-20 bg-slate-50 relative overflow-hidden">
            {/* Background Texture */}
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none" />

            <div className="container mx-auto px-4 mb-8">
                <div className="text-center">
                    <h2 className="text-3xl font-black text-slate-900 mb-2">
                        Why We Are The <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-violet-500">Anomalies</span>
                    </h2>
                    <p className="text-sm text-slate-500 font-medium">
                        Not just a website. Digital dominance.
                    </p>
                </div>
            </div>

            {/* Story Progress Bars */}
            <div className="container mx-auto px-4 mb-6 flex gap-1.5 justify-center">
                {anomalies.map((_, idx) => (
                    <div key={idx} className="h-1 flex-1 bg-slate-200 rounded-full overflow-hidden max-w-[40px]">
                        <motion.div
                            className={`h-full ${idx <= activeIndex ? 'bg-indigo-600' : 'bg-transparent'}`}
                            initial={false}
                            animate={{
                                width: idx < activeIndex ? '100%' : idx === activeIndex ? '100%' : '0%',
                                opacity: idx <= activeIndex ? 1 : 0
                            }}
                            transition={{ duration: 0.3 }}
                        />
                    </div>
                ))}
            </div>

            {/* Horizontal Snap Scroll Container */}
            <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="flex overflow-x-auto snap-x snap-mandatory pb-8 px-4 gap-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                style={{ scrollBehavior: 'smooth' }}
            >
                {anomalies.map((item, index) => (
                    <div
                        key={item.id}
                        className="flex-shrink-0 w-full snap-center relative [scroll-snap-stop:always]"
                    >
                        <div className={`h-[420px] rounded-[2.5rem] p-8 flex flex-col justify-between relative overflow-hidden bg-white border border-slate-100 shadow-xl`}>

                            {/* Card Background Decoration */}
                            <div className={`absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-${item.color}-500/10 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/2`} />

                            {/* Icon / Top Content */}
                            <div className="relative z-10">
                                <div className={`w-16 h-16 rounded-2xl bg-${item.color}-50 flex items-center justify-center mb-8 shadow-sm`}>
                                    <Icon name={item.icon} className={`w-8 h-8 text-${item.color}-500`} />
                                </div>
                                <h3 className="text-3xl font-black text-slate-900 mb-4 leading-tight">
                                    {item.title}
                                </h3>
                            </div>

                            {/* Bottom Content / Description */}
                            <div className="relative z-10">
                                <p className="text-slate-600 text-lg leading-relaxed font-medium">
                                    {item.desc}
                                </p>

                                {item.isCta && (
                                    <button
                                        onClick={() => setIsModalOpen(true)}
                                        className="mt-8 w-full py-4 bg-indigo-600 text-white font-bold rounded-xl shadow-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
                                    >
                                        Start Now
                                        <Icon name="ArrowRightIcon" className="w-4 h-4" />
                                    </button>
                                )}
                            </div>

                            {/* Visual Element for Speed (First Card) */}
                            {item.id === 'speed' && (
                                <div className="absolute bottom-0 right-0 w-40 h-24 opacity-10">
                                    <svg viewBox="0 0 100 60" className="w-full h-full text-indigo-600 fill-current">
                                        <path d="M0 60 L20 40 L40 50 L60 20 L80 30 L100 0 L100 60 Z" />
                                    </svg>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Pagination Dots */}
            <div className="flex justify-center gap-2 mb-8">
                {anomalies.map((_, idx) => (
                    <div
                        key={idx}
                        className={`h-2 rounded-full transition-all duration-300 ${idx === activeIndex ? 'w-6 bg-indigo-600' : 'w-2 bg-slate-300'}`}
                    />
                ))}
            </div>

            <ScheduleModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </section>
    );
};

export default MobileWhyChooseUs;
